import { t } from "i18next";
import { Loader2, Paperclip } from "lucide-react";
import type { JSX } from "react";
import { useRef, useState } from "react";
import { AppConfig } from "@/common/appConfig";
import { ValidationSuscriptionButton } from "@/common/enums/enums";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { V3Drawer } from "@/components/ui/v3/drawer";
import { underV3 } from "@/components/ui/v3/flagged";
import { toast } from "@/hooks/use-toast";
import { useNoteGeneration } from "@/hooks/visits/useNoteGeneration";
import { useVisitReads } from "@/hooks/visits/VisitReads";
import { cn } from "@/lib/utils";
import type { RecorderFormSchemaType } from "@/schemas/newVisit.schema";
import RecorderForm, {
  type RecorderFormHandle,
} from "../audio-recorder/recorder-form";
import ConfirmDialog from "../ConfirmDialog";
import { SubscriptionOnlyButton } from "../SubscriptionOnlyButton/SubscriptionOnlyButton";
import { DocumentGenerationProgress } from "./DocumentGenerationProgress";
import { NoteGenerationNotice } from "./NoteGenerationNotice";

export function NewNoteSidebar({
  open,
  onOpenChange,
  sourceIds = [],
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Visit sources selected in the assistant panel.
  sourceIds?: readonly number[];
}): JSX.Element {
  const { file } = useVisitReads();
  const {
    submit,
    retry,
    abandon,
    loading,
    submitting,
    error,
    recovery,
    isCurrent,
    beginDraft,
  } = useNoteGeneration({ visitPublicId: file?.public_id, open });
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const recorderFormRef = useRef<RecorderFormHandle>(null);
  const accept = async (operation: Promise<boolean>): Promise<void> => {
    if ((await operation) && isCurrent()) {
      toast({
        variant: "success",
        title: t("visit.messages.createSuccessful"),
      });
      onOpenChange(false);
    }
  };
  const handleFormSubmit = async (
    form: RecorderFormSchemaType,
  ): Promise<void> => {
    if (loading || recovery || !beginDraft()) return;
    await accept(
      submit({
        template: form.templateName,
        style: form.style,
        format: form.format,
        exam: form.examination,
        customTemplatePublicId: form.customTemplatePublicId,
        macroPublicIds: [],
        ...(sourceIds.length ? { sourceIds: [...sourceIds] } : {}),
      }),
    );
  };
  const preventWhileLoading = (event: Event): void => {
    if (loading) event.preventDefault();
  };
  // v3.2 app: the same form in the v3 drawer (sentence-case title, the note
  // settings as one card of pickers, one primary action; the X cancels).
  if (AppConfig.enableAppRedesign) {
    return (
      <V3Drawer
        footer={
          !recovery && (
            <SubscriptionOnlyButton
              className="h-12 w-full text-[15px]"
              disabled={loading || !file}
              validations={[ValidationSuscriptionButton.RECORDING_USAGES]}
              onClick={async () => {
                await recorderFormRef.current?.submitForm();
              }}
            >
              {loading && <Loader2 aria-hidden className="animate-spin" />}
              {loading
                ? t("visit.sidebar.newNote.writing")
                : t("visit.sidebar.newNote.writeNote")}
            </SubscriptionOnlyButton>
          )
        }
        focusPanel
        open={open}
        testId="new-note-sheet"
        title={t("visit.sidebar.newNote.titleV3")}
        onEscapeKeyDown={preventWhileLoading}
        onOpenChange={(value) => {
          if (!loading) onOpenChange(value);
        }}
      >
        <div className="flex flex-col gap-4 px-5 pb-4">
          <p className="text-[14px] leading-5 text-v3-text-2">
            {t("visit.sidebar.newNote.sourcesHint")}
          </p>
          {sourceIds.length > 0 && (
            <p
              className="flex items-center gap-2 rounded-xl bg-v3-surface-2 px-3 py-2.5 text-[13.5px] text-v3-text-2 shadow-v3-hairline"
              data-testid="new-note-sources"
            >
              <Paperclip aria-hidden className="size-4 shrink-0" />
              {t("visit.workspace.sources.newNoteIncludes", {
                count: sourceIds.length,
              })}
            </p>
          )}
          {loading && (
            <DocumentGenerationProgress kind="note" opensHere={false} />
          )}
          {!recovery && (
            <RecorderForm
              ref={recorderFormRef}
              onSubmitData={handleFormSubmit}
            />
          )}
          <NoteGenerationNotice
            error={error}
            recovery={recovery}
            loading={loading}
            submitting={submitting}
            onRetry={() => accept(retry())}
            onAbandon={abandon}
          />
        </div>
      </V3Drawer>
    );
  }
  return (
    <Sheet
      open={open}
      onOpenChange={(value) => {
        if (!loading) onOpenChange(value);
      }}
    >
      <SheetContent
        className="w-full sm:max-w-md p-0 sm:rounded-l-2xl overflow-y-auto"
        onEscapeKeyDown={preventWhileLoading}
        onInteractOutside={preventWhileLoading}
      >
        <div className="flex flex-col min-h-full">
          <SheetHeader
            className={cn(
              "px-10 pt-6",
              // v3.2: title on the left, as the other v3.2 sheets.
              underV3("px-5 pt-5 pr-14 text-left"),
            )}
          >
            <SheetTitle>{t("visit.generateNote")}</SheetTitle>
          </SheetHeader>
          <div
            className={cn(
              "flex-1 flex flex-col gap-6 py-6 px-10",
              underV3("px-5"),
            )}
          >
            <p>{t("visit.sidebar.newNote.sourcesHint")}</p>
            {sourceIds.length > 0 && (
              <p
                className="rounded-lg border bg-muted/40 p-3 text-sm"
                data-testid="new-note-sources"
              >
                {t("visit.workspace.sources.newNoteIncludes", {
                  count: sourceIds.length,
                })}
              </p>
            )}
            {loading && (
              <DocumentGenerationProgress kind="note" opensHere={false} />
            )}
            {!recovery && (
              <RecorderForm
                ref={recorderFormRef}
                onSubmitData={handleFormSubmit}
              />
            )}
            <NoteGenerationNotice
              error={error}
              recovery={recovery}
              loading={loading}
              submitting={submitting}
              onRetry={() => accept(retry())}
              onAbandon={abandon}
            />
          </div>
          <SheetFooter
            className={cn(
              "bg-tinted-grey-1 rounded-bl-2xl flex flex-col sm:space-x-0 sm:flex-col sm:justify-center content-center px-10 py-6 gap-2",
              underV3(
                "bg-transparent px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]",
              ),
            )}
          >
            {!recovery && (
              <SubscriptionOnlyButton
                className="w-full"
                disabled={loading || !file}
                size="xl"
                validations={[ValidationSuscriptionButton.RECORDING_USAGES]}
                onClick={async () => {
                  await recorderFormRef.current?.submitForm();
                }}
              >
                {loading
                  ? `${t("visit.sidebar.newNote.processing")}...`
                  : t("visit.sidebar.newNote.submit")}
              </SubscriptionOnlyButton>
            )}
            <ConfirmDialog
              open={showCancelConfirmation}
              onOpenChange={setShowCancelConfirmation}
              title={t("visits.sidebar.cancelConfirmation.title")}
              description={t("visits.sidebar.cancelConfirmation.description")}
              trigger={
                <Button
                  className="w-full"
                  disabled={loading}
                  size="xl"
                  variant="grey"
                >
                  {t("visits.sidebar.cancel")}
                </Button>
              }
              onConfirm={() => {
                if (!loading) onOpenChange(false);
              }}
            />
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
}
